import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const SignUp = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await signUp(formData);
      navigate('/');
    } catch (error) {
      setError('Failed to create account. ' + error.message);
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page dark:bg-page-dark">
      <div className="max-w-md w-full mx-4 p-8 rounded-lg shadow-lg bg-surface dark:bg-surface-dark">
        <h2 className="text-2xl font-bold mb-6 text-center text-text-primary dark:text-text-primary-dark">
          Create Account
        </h2>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-status-error-bg dark:bg-status-error-bg-dark text-status-error-text dark:text-status-error-text-dark">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-text-secondary dark:text-text-secondary-dark">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-text-secondary dark:text-text-secondary-dark">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-text-secondary dark:text-text-secondary-dark">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-text-secondary dark:text-text-secondary-dark">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-text-secondary dark:text-text-secondary-dark">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark border-input-border dark:border-input-border-dark focus:ring-2 focus:ring-button focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary dark:text-text-tertiary-dark dark:hover:text-text-primary-dark"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full p-3 rounded-lg bg-button hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark text-text-primary-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="mt-4 text-sm text-center text-text-tertiary dark:text-text-tertiary-dark">
          Already have an account?{' '}
          <Link to="/login" className="text-text-link dark:text-text-link-dark hover:text-text-link-hover dark:hover:text-text-link-hover-dark">
            Sign in
          </Link>
        </p>

        <p className="mt-8 text-sm text-center text-text-tertiary dark:text-text-tertiary-dark">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default SignUp; 